import { Spin, Table } from "antd";
import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { Locale } from "../../utils";
import {
  handleMutationError,
  showNotificationWithIcon,
} from "../../utils/helpers/helpers";
import { getList, remove } from "../../utils/http/dataProvider";
import { duplicateCustomPage, publishCustomPage } from "../../utils/http/customPageDataProvider";
import { getUserInfo } from "../../utils/redux/reducers/User";
import { BreadCrumb } from "../../components/main/LayoutComponents";

import "./CustomPageList.scss";
import { customPageListColumns } from "./customPageListColumns";
import CustomPageListHeader from "./CustomPageListHeader";

const i18n = Locale.getInstance();

const CustomPageList = () => {
  const user = useSelector((state) => getUserInfo(state));
  const queryClient = useQueryClient();
  const [pageType, setPageType] = useState(undefined);
  const [language, setLanguage] = useState(undefined);
  const [status, setStatus] = useState(undefined);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const history = useHistory();
  const [perPage, setPerPage] = useState(20);

  // Reset page to 1 when filters change
  useEffect(() => {
    setPage(1);
  }, [pageType, language, status]);

  const filter = {};
  if (pageType) filter.page_type = pageType;
  if (language) filter.language = language;
  if (status) filter.status = status;

  const { data, error, isLoading } = useQuery(
    ["custom-pages", user.token, page, perPage, filter],
    () =>
      getList("custom-pages", {
        pagination: {
          perPage,
          page,
        },
        sort: {
          field: "id",
          order: "desc",
        },
        filter,
        token: user.token,
      })
  );

  if (error) {
    showNotificationWithIcon(error.data?.message || error.message);
  }

  const deleteMutation = useMutation(remove, {
    onSuccess: () => {
      queryClient.invalidateQueries("custom-pages");
      setLoading(false);
      showNotificationWithIcon(i18n.t("media.saveFinished"), "success");
    },
    onError: (error) => {
      setLoading(false);
      handleMutationError(error);
    },
  });

  const duplicateMutation = useMutation(
    ({ id, token }) => duplicateCustomPage(id, token),
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries("custom-pages");
        setLoading(false);
        showNotificationWithIcon(i18n.t("customPage.duplicateSuccess"), "success");
      },
      onError: (error) => {
        setLoading(false);
        handleMutationError(error);
      },
    }
  );

  const publishMutation = useMutation(
    ({ id, token }) => publishCustomPage(id, token),
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries("custom-pages");
        setLoading(false);
        showNotificationWithIcon(i18n.t("customPage.publishSuccess"), "success");
      },
      onError: (error) => {
        setLoading(false);
        handleMutationError(error);
      },
    }
  );

  return (
    <>
      <BreadCrumb data={[{ title: i18n.t("customPage.title") }]} />
      <Spin spinning={loading || isLoading}>
        <CustomPageListHeader
          pageType={pageType}
          setPageType={setPageType}
          language={language}
          setLanguage={setLanguage}
          status={status}
          setStatus={setStatus}
        />
        <Table
          className="list"
          rowKey="id"
          columns={customPageListColumns(
            deleteMutation,
            duplicateMutation,
            publishMutation,
            user.token,
            setLoading,
            history
          )}
          dataSource={data?.data}
          scroll={{
            y: 600,
          }}
          pagination={{
            current: page,
            total: data?.total,
            pageSize: perPage,
            onChange: (newPage) => {
              setPage(newPage);
            },
          }}
        />
      </Spin>
    </>
  );
};

export default CustomPageList;

